import { useState } from "react";
import { FlowGraph, AirdropList } from "@/lib/algo/graph";
import { extractHolders } from "@/lib/algo/heliusReconstruct";
import { useApp } from "@/context/AppCtx";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { tokenMetaCache } from "@/lib/tokenMetaCache";

import { Users } from "lucide-react";

export function AirdropsTab({
  graph,
  update,
  activeMint,
}: {
  graph: FlowGraph;
  update: (fn: (g: FlowGraph) => void) => void;
  activeMint: string;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [retryingMap, setRetryingMap] = useState<Record<string, boolean>>({});
  const { globalAirdropLists, setGlobalAirdropLists, addAppToast } = useApp() as any;

  const handleExtract = async () => {
    if (!activeMint || activeMint.length < 32) {
      addAppToast("Please select a valid token mint from the Nursery first.", "error");
      return;
    }

    setLoading(true);
    try {
      const list = await extractHolders(activeMint);
      setGlobalAirdropLists([...globalAirdropLists, list]);
      
      // Also add to current graph for playground usage
      update((g) => {
        if (!g.airdropLists) g.airdropLists = [];
        g.airdropLists.push(list);
      });
      
      const meta = tokenMetaCache.get(activeMint);
      const ticker = meta?.symbol ? `$${meta.symbol}` : 'token';
      addAppToast(`Saved ${list.recipients.length} wallets from ${ticker}`, "info");
    } catch (e: any) {
      addAppToast("Failed to extract: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (list: AirdropList) => {
    const mint = list.sourceMint || list.mint || (list.id && list.id.length >= 32 ? list.id : undefined);
    if (!mint || mint.length < 32) {
      addAppToast("No valid mint address associated with this list.", "error");
      return;
    }

    setRetryingMap((prev) => ({ ...prev, [list.id]: true }));
    try {
      const newList = await extractHolders(mint);
      
      // Keep original list's ID so references in graph (e.g. edges) stay valid
      const updatedList = { ...newList, id: list.id };
      if (list.sourceMint) updatedList.sourceMint = list.sourceMint;
      if (list.mint) updatedList.mint = list.mint;

      // Update global list
      const updatedGlobal = globalAirdropLists.map((l: AirdropList) =>
        l.id === list.id ? updatedList : l
      );
      setGlobalAirdropLists(updatedGlobal);

      // Also update current graph list
      update((g) => {
        if (g.airdropLists) {
          g.airdropLists = g.airdropLists.map((l: AirdropList) =>
            l.id === list.id ? updatedList : l
          );
        }
      });

      const meta = tokenMetaCache.get(mint);
      const ticker = meta?.symbol ? `$${meta.symbol}` : 'token';
      addAppToast(`Saved ${newList.recipients.length} wallets from ${ticker}`, "info");
    } catch (e: any) {
      addAppToast("Failed to extract: " + e.message, "error");
    } finally {
      setRetryingMap((prev) => ({ ...prev, [list.id]: false }));
    }
  };

  const removeList = (id: string) => {
    setGlobalAirdropLists(globalAirdropLists.filter((l: AirdropList) => l.id !== id));
    update((g) => {
      if (g.airdropLists) {
        g.airdropLists = g.airdropLists.filter((l: AirdropList) => l.id !== id);
      }
    });
  };

  // Combine global lists with any lists that might be local only
  const combinedListsMap = new Map<string, AirdropList>();
  globalAirdropLists.forEach((l: AirdropList) => combinedListsMap.set(l.id, l));
  if (graph.airdropLists) {
    graph.airdropLists.forEach((l: AirdropList) => combinedListsMap.set(l.id, l));
  }
  const displayLists = Array.from(combinedListsMap.values());

  return (
    <div className="p-4 flex flex-col gap-4 h-full overflow-y-auto">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-emerald-400">{t("airdrop.title")}</h3>
        <button
          className="btn btn-sm bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
          onClick={handleExtract}
          disabled={loading}
        >
          {loading ? t("airdrop.extracting") : t("airdrop.extract")}
        </button>
      </div>

      <div className="text-sm text-white/50">
        {t("airdrop.desc")}
      </div>

      <div className="flex flex-col gap-3">
        {displayLists.length === 0 ? (
          <div className="text-center p-8 bg-black/20 rounded border border-[var(--line)] text-white/40 text-sm">
            {t("airdrop.empty")}
          </div>
        ) : (
          displayLists.map((list: AirdropList) => {
            const hasSourceMint = !!(list.sourceMint || list.mint || (list.id && list.id.length >= 32));
            return (
              <div key={list.id} className="bg-[var(--panel)] border border-[var(--line)] rounded p-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-white">{list.name}</div>
                  <div className="flex items-center gap-2">
                    {hasSourceMint && (
                      <button
                        className="text-emerald-400 text-xs hover:underline disabled:opacity-50"
                        onClick={() => handleRetry(list)}
                        disabled={retryingMap[list.id]}
                      >
                        {retryingMap[list.id] ? t("airdrop.retrying") : t("airdrop.retry")}
                      </button>
                    )}
                    <button className="text-red-400 text-xs hover:underline" onClick={() => removeList(list.id)}>
                      {t("airdrop.remove")}
                    </button>
                  </div>
                </div>
                <div className="text-xs text-white/50 flex items-center gap-2">
                  <Users size={12} /> {t("airdrop.recipients", { count: list.recipients.length })}
                </div>
                <div className="max-h-32 overflow-y-auto bg-black/30 rounded border border-white/5 p-2 mt-1">
                  {list.recipients.slice(0, 50).map((r: any, i: number) => (
                    <div key={i} className="flex justify-between text-[10px] font-mono mb-1 last:mb-0">
                      <span className="text-white/70">{r.address.slice(0,8)}...{r.address.slice(-6)}</span>
                      <span className="text-emerald-400">{r.pct.toFixed(2)}%</span>
                    </div>
                  ))}
                  {list.recipients.length > 50 && (
                    <div className="text-[10px] text-white/40 text-center mt-2 italic">
                      {t("airdrop.more", { count: list.recipients.length - 50 })}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

